import React, { useState, useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import { useMediaQuery, useTheme, Box } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { useAppSettings } from '../../../../../../../app/features/settings/providers/AppSettingsProvider';
import useAppFormatAmount from '../../../../../../../app/features/format/hooks/useAppFormatAmount';
import useAppFormatNumber from '../../../../../../../app/features/format/hooks/useAppFormatNumber';
import styleVariables from '../../../../../../../app/styles/styleVariables';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import investmentKpiLang from '../../../../../langs/investmentKpiLang';
import AppFormatAmount from '../../../../../../../app/features/format/AppFormatAmount';
import { TInvestFundCurrency } from '../../../../../types/fund/TInvestFundCurrency';
import CGraphMultiCurrency from '../../../../../components/CGraphMultiCurrency';

interface IChartDataCommitted {
	committedCapital: number;
	sliceColor: string;
}

interface IChartDataCalled {
	calledCapital: number;
	sliceColor: string;
	settings?: {
		opacity: number;
	};
	tickSettings?: {
		opacity: number;
	};
}

type CSynthesisGraphCapitalProps = {
	color?: string;
	position: number;
	calledCapital: number;
	committedCapital: number;
	currency: TInvestFundCurrency;
};

const CSynthesisGraphCapital = (props: CSynthesisGraphCapitalProps) => {
	const { color, position, calledCapital, committedCapital, currency } = props;

	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();
	const { formatPercent } = useAppFormatNumber();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const [percentCommittedCapital] = useState(0);
	const [chartDataCommitted, setChartDataCommitted] = useState<IChartDataCommitted[]>([]);
	const [chartDataCalled, setChartDataCalled] = useState<IChartDataCalled[]>([]);

	useEffect(() => {
		const listColors = !darkModeEnabled
			? styleVariables.sliceColors.lightMode.map((x) => am5.color(x))
			: styleVariables.sliceColors.darkMode.map((x) => am5.color(x));

		const dataCalled = [] as IChartDataCalled[];
		const dataCommitted = [] as IChartDataCommitted[];

		// COMMITED
		dataCommitted.push({
			committedCapital: displayRounding(committedCapital, rounding),
			sliceColor: listColors[1 % listColors.length].toString(),
		});

		// CALLED
		dataCalled.push({
			calledCapital: displayRounding(calledCapital, rounding),
			sliceColor: listColors[(0 + 1) % listColors.length].toString(),
		});

		// ADD TRANSPARENT SLICE
		dataCalled.push({
			calledCapital: displayRounding(committedCapital - calledCapital, rounding),
			sliceColor:
				!darkModeEnabled && color === 'primary'
					? theme.palette.primary.main.toString()
					: theme.palette.background.paper.toString(),
			settings: {
				opacity: 0,
			},
			tickSettings: {
				opacity: 0,
			},
		});

		setChartDataCalled(dataCalled);
		setChartDataCommitted(dataCommitted);
	}, [
		committedCapital,
		calledCapital,
		t,
		darkModeEnabled,
		formatPercent,
		displayRounding,
		rounding,
		displayRoundingSuffix,
		theme.palette,
		color,
	]);

	useEffect(() => {
		if (position !== undefined && chartDataCalled.length > 0 && chartDataCommitted.length > 0 && currency) {
			const root = am5.Root.new('synthesis-graph-capital-' + position.toString());
			if (darkModeEnabled) {
				root.setThemes([am5themes_Dark.new(root)]);
			} else {
				root.setThemes([am5themes_Animated.new(root)]);
			}

			if (i18n.language === 'fr') {
				root.locale = am5locales_fr_FR;
			} else {
				root.locale = am5locales_en_US;
			}

			const chart = root.container.children.push(
				am5percent.PieChart.new(root, {
					layout: root.verticalLayout,
					innerRadius: am5.percent(60),
				}),
			);

			// CALLED
			const calledCapitalSerie = chart.series.push(
				am5percent.PieSeries.new(root, {
					valueField: 'calledCapital',
					alignLabels: true,
				}),
			);

			calledCapitalSerie?.get('colors')?.set(
				'colors',
				chartDataCalled.map((x: IChartDataCalled) => am5.color(x.sliceColor)),
			);

			calledCapitalSerie.ticks.template.setAll({ forceHidden: true });
			calledCapitalSerie.labels.template.setAll({ forceHidden: true });
			calledCapitalSerie.slices.template.states.create('hover', { scale: 1 });
			calledCapitalSerie.slices.template.setAll({
				tooltipText: undefined,
				forceInactive: true,
			});

			calledCapitalSerie.data.setAll(chartDataCalled);
			calledCapitalSerie.appear(1000, 100);

			// COMMITTED
			const committedCapitalSerie = chart.series.push(
				am5percent.PieSeries.new(root, {
					valueField: 'committedCapital',
					alignLabels: true,
					innerRadius: am5.percent(50),
				}),
			);

			committedCapitalSerie?.get('colors')?.set(
				'colors',
				chartDataCommitted.map((x: IChartDataCommitted) => am5.color(x.sliceColor)),
			);

			committedCapitalSerie.ticks.template.setAll({ forceHidden: true });
			committedCapitalSerie.labels.template.setAll({ forceHidden: true });
			committedCapitalSerie.slices.template.states.create('hover', { scale: 1 });
			committedCapitalSerie.slices.template.setAll({
				tooltipText: undefined,
				forceInactive: true,
				scale: 1.1,
			});

			committedCapitalSerie.data.setAll(chartDataCommitted);

			committedCapitalSerie.appear(1000, 100);

			chart.seriesContainer.children.push(
				am5.Label.new(root, {
					textAlign: 'center',
					y: 32,
					centerY: am5.percent(100),
					fill: am5.Color.fromString(
						color === 'primary' ? theme.palette.primary.contrastText : theme.palette.primary.main,
					),
					centerX: am5.p50,
					text: `[bold fontSize:30px]${displayRounding(calledCapital, rounding)} ${displayRoundingSuffix(
						rounding,
					)}${displayCurrencySymbol(currency)}[/]\n[bold fontSize:1.125rem]${t(
						investmentKpiLang.capital_called.tag,
					)}[/]`,
				}),
			);

			return () => {
				root.dispose();
			};
		}
	}, [
		chartDataCommitted,
		chartDataCalled,
		darkModeEnabled,
		percentCommittedCapital,
		t,
		isMobile,
		rounding,
		i18n.language,
		displayRoundingSuffix,
		displayCurrencySymbol,
		position,
		theme.palette.primary.main,
		displayRounding,
		calledCapital,
		color,
		theme.palette.primary.contrastText,
		currency,
	]);

	return (
		<>
			{position !== undefined ? (
				<>
					{currency ? (
						<Box
							id={`synthesis-graph-capital-${position.toString()}`}
							style={{ height: '380px', margin: '0px', padding: '0px' }}
						/>
					) : (
						<CGraphMultiCurrency height={380} />
					)}
					<Box sx={{ fontSize: '1.125rem', textAlign: 'center' }}>
						{t(investmentKpiLang.committed_capital.tag)}{' '}
						<Box component="span" className="commited-capital-value" sx={{ fontWeight: 600 }}>
							<AppFormatAmount value={committedCapital} currency={currency} />
						</Box>
					</Box>
				</>
			) : (
				''
			)}
		</>
	);
};

export default CSynthesisGraphCapital;
